'use client';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createTextNode,
  $getRoot,
  $isElementNode,
  COMMAND_PRIORITY_CRITICAL,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_TAB_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  extractSlashCommandQuery,
  filterSlashCommands,
  parseSlashCommandInput,
  resolveComposerKeyboardShortcut,
  resolveDirectSlashCommandInvocation,
  SlashCommandMenu,
  type SlashCommandItem,
} from '../../../slash-commands';
import { resetEditorToEmptyParagraph } from './editorParagraphNormalization';

const consumedEnterEvents = new WeakSet<Event>();

export function markEnterEventConsumed(event: Event | null | undefined): void {
  if (event) {
    consumedEnterEvents.add(event);
  }
}

export function isEnterEventConsumed(event: Event | null | undefined): boolean {
  return Boolean(event && consumedEnterEvents.has(event));
}

export type SlashCommandsAndShortcutsPluginProps = {
  slashCommands?: readonly SlashCommandItem[];
  isLoading?: boolean;
  isDisabled?: boolean;
  onSubmit?: () => void;
  onStop?: () => void;
  onCycleMode?: () => void;
};

export function SlashCommandsAndShortcutsPlugin({
  slashCommands = [],
  isLoading = false,
  isDisabled = false,
  onSubmit,
  onStop,
  onCycleMode,
}: SlashCommandsAndShortcutsPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [editorText, setEditorText] = useState('');
  const [dismissedQuery, setDismissedQuery] = useState<string | null>(null);
  const [forcedOpen, setForcedOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        setEditorText($getRoot().getTextContent());
      });
    });
  }, [editor]);

  const parsedSlash = useMemo(
    () => parseSlashCommandInput(editorText),
    [editorText],
  );

  const slashQuery = useMemo(
    () => extractSlashCommandQuery(editorText),
    [editorText],
  );

  const isTypingSlashQuery = slashCommands.length > 0 && slashQuery !== null;
  const query = slashQuery ?? '';

  const filteredCommands = useMemo(
    () =>
      slashCommands.length > 0
        ? filterSlashCommands({ commands: slashCommands, query })
        : [],
    [slashCommands, query],
  );

  const isMenuOpen =
    !isDisabled &&
    slashCommands.length > 0 &&
    ((forcedOpen && isTypingSlashQuery) ||
      (isTypingSlashQuery && dismissedQuery !== editorText)) &&
    filteredCommands.length > 0;

  useEffect(() => {
    setSelectedIndex(0);
    if (!isTypingSlashQuery) {
      setDismissedQuery(null);
      setForcedOpen(false);
    }
  }, [editorText, isTypingSlashQuery]);

  const clearEditorText = useCallback(() => {
    editor.update(() => {
      resetEditorToEmptyParagraph();
    });
    editor.focus();
  }, [editor]);

  const setEditorSlashPrefix = useCallback(() => {
    setEditorText('/');
    editor.update(() => {
      resetEditorToEmptyParagraph();
      const root = $getRoot();
      const paragraph = root.getFirstChild();

      if ($isElementNode(paragraph)) {
        const textNode = $createTextNode('/');

        paragraph.append(textNode);
        textNode.select(1, 1);
      }
    });
    editor.focus();
  }, [editor]);

  const executeCommand = useCallback(
    (command: SlashCommandItem, rawArgsOverride?: string) => {
      setForcedOpen(false);
      setDismissedQuery(null);
      clearEditorText();
      void command.onExecute?.({
        rawArgs: rawArgsOverride ?? parsedSlash?.rawArgs ?? '',
      });
    },
    [clearEditorText, parsedSlash?.rawArgs],
  );

  const isMenuOpenRef = useRef(isMenuOpen);
  const filteredCommandsRef = useRef(filteredCommands);
  const selectedIndexRef = useRef(selectedIndex);
  const editorTextRef = useRef(editorText);
  const slashCommandsRef = useRef(slashCommands);
  const onSubmitRef = useRef(onSubmit);

  isMenuOpenRef.current = isMenuOpen;
  filteredCommandsRef.current = filteredCommands;
  selectedIndexRef.current = selectedIndex;
  editorTextRef.current = editorText;
  slashCommandsRef.current = slashCommands;
  onSubmitRef.current = onSubmit;

  useEffect(() => {
    if (isDisabled) {
      return;
    }

    const unregUp = editor.registerCommand(
      KEY_ARROW_UP_COMMAND,
      (event) => {
        if (
          !isMenuOpenRef.current ||
          filteredCommandsRef.current.length === 0
        ) {
          return false;
        }

        event?.preventDefault();
        setSelectedIndex((prev) =>
          prev <= 0 ? filteredCommandsRef.current.length - 1 : prev - 1,
        );

        return true;
      },
      COMMAND_PRIORITY_CRITICAL,
    );

    const unregDown = editor.registerCommand(
      KEY_ARROW_DOWN_COMMAND,
      (event) => {
        if (
          !isMenuOpenRef.current ||
          filteredCommandsRef.current.length === 0
        ) {
          return false;
        }

        event?.preventDefault();
        setSelectedIndex(
          (prev) => (prev + 1) % filteredCommandsRef.current.length,
        );

        return true;
      },
      COMMAND_PRIORITY_CRITICAL,
    );

    const unregEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (event?.shiftKey || event?.isComposing) {
          return false;
        }

        const currentParsed = parseSlashCommandInput(editorTextRef.current);

        if (currentParsed?.hasSpaceAfterCommand) {
          const directInvocation = resolveDirectSlashCommandInvocation({
            commands: slashCommandsRef.current,
            input: editorTextRef.current,
          });

          if (directInvocation?.command.onExecute) {
            event?.preventDefault();
            markEnterEventConsumed(event);
            executeCommand(directInvocation.command, directInvocation.rawArgs);

            return true;
          }
        } else if (
          isMenuOpenRef.current &&
          filteredCommandsRef.current.length > 0
        ) {
          const chosen =
            filteredCommandsRef.current[selectedIndexRef.current] ??
            filteredCommandsRef.current[0];

          if (chosen) {
            event?.preventDefault();
            markEnterEventConsumed(event);
            executeCommand(chosen);

            return true;
          }
        }

        if (onSubmitRef.current) {
          event?.preventDefault();
          markEnterEventConsumed(event);
          onSubmitRef.current();

          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );

    const unregTab = editor.registerCommand(
      KEY_TAB_COMMAND,
      (event) => {
        if (event.shiftKey) {
          if (onCycleMode) {
            event.preventDefault();
            onCycleMode();

            return true;
          }

          return false;
        }

        if (
          !isMenuOpenRef.current ||
          filteredCommandsRef.current.length === 0
        ) {
          return false;
        }

        const chosen =
          filteredCommandsRef.current[selectedIndexRef.current] ??
          filteredCommandsRef.current[0];

        if (!chosen) {
          return false;
        }

        event.preventDefault();
        executeCommand(chosen);

        return true;
      },
      COMMAND_PRIORITY_CRITICAL,
    );

    const unregEscape = editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      (event) => {
        if (isMenuOpenRef.current) {
          event.preventDefault();
          setForcedOpen(false);
          setDismissedQuery(editorTextRef.current);

          return true;
        }

        if (isLoading && onStop) {
          event.preventDefault();
          onStop();

          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );

    return () => {
      unregUp();
      unregDown();
      unregEnter();
      unregTab();
      unregEscape();
    };
  }, [editor, executeCommand, isDisabled, isLoading, onCycleMode, onStop]);

  useEffect(() => {
    if (isDisabled) {
      return;
    }

    const handleRootKeyDown = (event: KeyboardEvent) => {
      const action = resolveComposerKeyboardShortcut({
        event: {
          key: event.key,
          shiftKey: event.shiftKey,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
          altKey: event.altKey,
        },
        isSlashMenuOpen: isMenuOpenRef.current,
        isStreaming: Boolean(isLoading),
      });

      if (action === 'open-command-palette' && slashCommands.length > 0) {
        event.preventDefault();
        setDismissedQuery(null);
        setForcedOpen(true);
        if (extractSlashCommandQuery(editorTextRef.current) === null) {
          setEditorSlashPrefix();
        } else {
          editor.focus();
        }

        return;
      }

      if (
        event.key === 'Enter' &&
        event.keyCode !== 13 &&
        !event.shiftKey &&
        !event.isComposing &&
        !isEnterEventConsumed(event)
      ) {
        editor.dispatchCommand(KEY_ENTER_COMMAND, event);
      }
    };

    return editor.registerRootListener((rootElement, prevRootElement) => {
      if (rootElement) {
        rootElement.addEventListener('keydown', handleRootKeyDown);
      }

      if (prevRootElement) {
        prevRootElement.removeEventListener('keydown', handleRootKeyDown);
      }
    });
  }, [
    editor,
    isDisabled,
    isLoading,
    setEditorSlashPrefix,
    slashCommands.length,
  ]);

  if (!isMenuOpen) {
    return null;
  }

  return (
    <SlashCommandMenu
      commands={filteredCommands}
      selectedIndex={selectedIndex}
      onSelect={(command) => executeCommand(command)}
      onHoverIndex={setSelectedIndex}
    />
  );
}
